import { useId } from 'react'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { RenderCategoryBreakdown } from '../../../insightRenderShapes'
import { Answer, BarList, InsightBar, Note } from '../InsightsBlocks'
import { InsightCardHeader } from './InsightCardHeader'
import { formatInsightDatum } from './formatInsightDatum'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../../../shopifyUiTracking'

/** Native bars format server-selected shares; every category remains in the details, without a top-ten limit. */
export function CategoryBreakdownCard({ card }: { card: RenderCategoryBreakdown }) {
  const shopify = useAppBridge()
  const detailsId = `category-details-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  let content = <s-paragraph color="subdued">{card.message}</s-paragraph>
  if (card.state === 'ready') {
    const bars = card.rows.slice(0, card.previewRows).map((row) => {
      const figure = `${formatInsightDatum(row.count)} · ${formatInsightDatum(row.share)}`
      if (row.share.result.state !== 'available')
        return (
          <Note key={row.key}>
            {row.label}: {formatInsightDatum(row.share)}
          </Note>
        )
      return (
        <InsightBar
          key={row.key}
          label={row.label}
          value={row.share.result.value}
          max={1}
          figure={figure}
          figureSuffix=""
          formatCount={String}
          accessibilityLabel={`${row.label}: ${figure}`}
        />
      )
    })
    content = (
      <s-stack gap="base">
        <BarList>{bars}</BarList>
        <s-link
          commandFor={detailsId}
          command="--show"
          onClick={() => trackShopifyAction(shopify, 'open-patterns', () => undefined)}
        >
          {card.detailsLabel}
        </s-link>
      </s-stack>
    )
  }
  return (
    <s-section heading={card.title}>
      <s-icon slot="graphic" type="product" />
      <InsightCardHeader title={card.title} help={card.help} />
      <s-stack gap="base">
        <Answer figure={formatInsightDatum(card.base)} label={card.base.label} />
        {content}
        <Note>{card.footer}</Note>
      </s-stack>
      <s-modal id={detailsId} heading={card.title}>
        <s-stack gap="base">
          <Note>
            {card.base.label}: {formatInsightDatum(card.base)}
          </Note>
          {card.rows.map((row) => (
            <s-stack key={row.key} gap="small">
              <s-text fontWeight="semibold">{row.label}</s-text>
              <s-text>
                {row.count.label}: {formatInsightDatum(row.count)}
              </s-text>
              <s-text>
                {row.share.label}: {formatInsightDatum(row.share)}
              </s-text>
              <s-divider />
            </s-stack>
          ))}
          <Note>{card.footer}</Note>
        </s-stack>
        <s-button
          slot="secondary-actions"
          commandFor={detailsId}
          command="--hide"
          onClick={() => trackShopifyAction(shopify, 'close-patterns', () => undefined)}
        >
          {shopifyComponentCopy.insightCategoryDetails.close}
        </s-button>
      </s-modal>
    </s-section>
  )
}
